import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../app/rootReducer';
import DepositosService from '../../../services/estoque/depositos';
import { Deposito } from '../../../@types/estoque/deposito';
import promiseWithLoaderAndMessages from '../../../utilities/promise-with-loader-and-messages';
import { HeaderParams } from '../../../@types/headers';
import { ListReturnType } from '../../../services/queryable-base';
import { toastExcludeMessages, falhaAoCarregar, toastSaveMessages } from '../../../utilities/default-confirmation-messages';
import queryable, { Query } from '../../../services/queryable';

interface DepositoState {
  depositos: Deposito[],
  pagination: HeaderParams,
}

const initialState: DepositoState = {
  depositos: [],
  pagination: {
    currentPage: undefined,
    totalRecords: undefined,
    maxPerPage: undefined,
    pages: undefined,
  },
};

export const fetchDepositos = createAsyncThunk<
  ListReturnType<Deposito>,
  undefined | Omit<Query<Deposito>, 'service'>
>(
  `${DepositosService.endpoint}/list`,
  (params = {}, { dispatch }) => {
    const query = queryable.query({
      ...params,
      service: DepositosService,
    });

    return promiseWithLoaderAndMessages(
      dispatch,
      query,
      { errorMessage: falhaAoCarregar('depósitos') },
    );
  },
);

export const deleteDeposito = createAsyncThunk(
  `${DepositosService.endpoint}/delete`,
  async (id: string, { dispatch }) => {
    await promiseWithLoaderAndMessages(
      dispatch,
      DepositosService.delete(id),
      toastExcludeMessages('depósito'),
    );

    return id;
  },
);

export const createOrUpdateDeposito = createAsyncThunk(
  `${DepositosService.endpoint}/createOrUpdate`,
  (data: Parameters<typeof DepositosService.createOrUpdate>[0], { dispatch, rejectWithValue }) => (
    promiseWithLoaderAndMessages(
      dispatch,
      DepositosService.createOrUpdate(data),
      toastSaveMessages('depósito'),
    ).catch((error) => rejectWithValue(error.response.data))
  ),
);

export const depositosSlice = createSlice({
  name: 'deposito',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(fetchDepositos.fulfilled, (state, { payload }) => {
      state.depositos = payload.data;
      state.pagination = payload.headers;
    });
  },
});

export const selectDepositoState = (state: RootState) => state.depositosReducer;

export default depositosSlice.reducer;
